import React, { useEffect, useRef, useState } from "react";
import api, { API } from "../lib/api";
import axios from "axios";
import {
  FileText, Image as ImageIcon, Upload, Trash2, Download,
  Loader2, Paperclip, ExternalLink,
} from "lucide-react";

const humanSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const iconFor = (mime) => {
  if ((mime || "").startsWith("image/")) return ImageIcon;
  return FileText;
};

const PatientDocuments = ({ patientId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMime, setPreviewMime] = useState("");
  const [previewName, setPreviewName] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/patients/${patientId}/documents`);
      setItems(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (patientId) load(); /* eslint-disable-next-line */ }, [patientId]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const doUpload = async (file) => {
    if (!file) return;
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("dotmedy_token");
      const res = await axios.post(
        `${API}/patients/${patientId}/documents`,
        fd,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setItems((prev) => [res.data, ...prev]);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openDoc = async (d) => {
    try {
      const res = await api.get(`/patients/${patientId}/documents/${d.id}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: d.mime_type });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewMime(d.mime_type);
      setPreviewName(d.filename);
    } catch {
      alert("Failed to open document");
    }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/patients/${patientId}/documents/${d.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: d.mime_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { alert("Download failed"); }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete ${d.filename}?`)) return;
    try {
      await api.delete(`/patients/${patientId}/documents/${d.id}`);
      setItems((prev) => prev.filter((x) => x.id !== d.id));
    } catch { alert("Failed to delete"); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) doUpload(f);
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Paperclip size={18} className="text-[#14B8A6]" />
          <h3 className="font-semibold text-slate-800">Patient Documents</h3>
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{items.length}</span>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,application/pdf" onChange={onFile} className="hidden" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 bg-[#14B8A6] hover:bg-[#0F8A7D] disabled:opacity-60 text-white text-sm px-3 py-2 rounded-md"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Upload
          </button>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={onDrop}
        className="border-2 border-dashed border-slate-200 rounded-lg py-6 text-center text-xs text-slate-500 mb-4 hover:border-[#14B8A6]/40 transition-colors"
      >
        Drop a file here or click <button type="button" onClick={() => fileRef.current?.click()} className="text-[#0F2A5F] font-semibold hover:underline">Upload</button>. Accepts JPG, PNG, PDF (max 10 MB).
      </div>

      {err && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}

      {loading ? (
        <div className="p-8 text-center text-slate-400 text-sm">Loading...</div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-slate-500 text-sm">No documents yet</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((d) => {
            const Icon = iconFor(d.mime_type);
            return (
              <li key={d.id} className="py-3 flex items-center justify-between gap-3">
                <button onClick={() => openDoc(d)} className="flex items-center gap-3 min-w-0 flex-1 text-left group">
                  <div className="w-10 h-10 rounded-lg bg-[#E6F7F5] text-[#0F8A7D] flex items-center justify-center flex-shrink-0">
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 group-hover:text-[#0F2A5F] truncate">{d.filename}</div>
                    <div className="text-xs text-slate-500">
                      {humanSize(d.size)} • {new Date(d.uploaded_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                  </div>
                </button>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openDoc(d)} title="Open" className="p-2 text-slate-500 hover:text-[#0F2A5F] hover:bg-slate-100 rounded">
                    <ExternalLink size={14} />
                  </button>
                  <button onClick={() => downloadDoc(d)} title="Download" className="p-2 text-slate-500 hover:text-[#0F2A5F] hover:bg-slate-100 rounded">
                    <Download size={14} />
                  </button>
                  <button onClick={() => del(d)} title="Delete" className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded">
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="text-sm font-medium text-slate-800 truncate">{previewName}</div>
              <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="text-slate-500 hover:text-slate-800 text-2xl leading-none">×</button>
            </div>
            <div className="flex-1 bg-slate-100 overflow-auto flex items-center justify-center">
              {previewMime === "application/pdf" ? (
                <iframe src={previewUrl} title={previewName} className="w-full h-full border-0" />
              ) : (
                <img src={previewUrl} alt={previewName} className="max-w-full max-h-full object-contain" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDocuments;
